import JSZip from "jszip";
import { buildSvgMarkup } from "./animation";
import { unionBbox } from "./canvasUtils";

// On an infinite canvas there is no fixed artboard, so the exported SVG frames
// the actual content (bounding box + padding). Falls back to a default frame
// when the canvas is empty.
function contentFrame(elements) {
  if (!elements || !elements.length) return { x: 0, y: 0, width: 800, height: 600 };
  const bb = unionBbox(elements);
  const pad = 24;
  return {
    x: Math.round(bb.x - pad),
    y: Math.round(bb.y - pad),
    width: Math.max(1, Math.round(bb.w + pad * 2)),
    height: Math.max(1, Math.round(bb.h + pad * 2)),
  };
}

export async function exportProjectZip(elements, { background = "transparent", name = "svg-studio-export" } = {}) {
  const frame = contentFrame(elements);
  const svg = buildSvgMarkup(elements, { ...frame, playing: true, background });
  const zip = new JSZip();
  zip.file(`${name}.svg`, svg);
  zip.file(
    "preview.html",
    `<!doctype html><html><head><meta charset="utf-8"/><title>${name} · Preview</title>
<style>body{margin:0;display:grid;place-items:center;min-height:100vh;background:#0a0a0a;font-family:system-ui;color:#fafafa}.frame{padding:24px;background:#fff;border:1px solid #262626}</style>
</head><body><div class="frame">${svg}</div></body></html>`
  );
  zip.file(
    "README.md",
    `# ${name}

Interactive SVG exported from **SVG Studio**.

## Files
- \`${name}.svg\` — Self-contained interactive SVG. Open it in a browser, or embed it via \`<img>\`, \`<object>\`, or inline.
- \`preview.html\` — A minimal HTML page that renders the SVG for quick preview.
- \`README.md\` — This file.

## Embedding
\`\`\`html
<img src="${name}.svg" alt="${name}" />
\`\`\`
Animations are pure CSS keyframes embedded in a \`<style>\` block inside the SVG.
Toggle the \`paused\` class on the root \`<svg>\` element to pause animation playback.

Generated on ${new Date().toISOString()}.
`
  );
  zip.file(
    "project.json",
    JSON.stringify({ name, ...contentFrame(elements), elements, generatedAt: new Date().toISOString() }, null, 2)
  );
  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(blob, `${name}.zip`);
}

export function exportSvgOnly(elements, { background = "transparent", name = "svg-studio" } = {}) {
  const frame = contentFrame(elements);
  const svg = buildSvgMarkup(elements, { ...frame, playing: true, background });
  const blob = new Blob([svg], { type: "image/svg+xml" });
  triggerDownload(blob, `${name}.svg`);
}

// ===== Selection / single-element export (multi-format + scale) =====
export const EXPORT_FORMATS = [
  { value: "svg", label: "SVG", ext: "svg" },
  { value: "png", label: "PNG", ext: "png", mime: "image/png" },
  { value: "jpg", label: "JPG", ext: "jpg", mime: "image/jpeg" },
  { value: "webp", label: "WebP", ext: "webp", mime: "image/webp" },
];
export const EXPORT_SCALES = [1, 2, 3, 4];

// Rasterize an SVG markup string to a PNG/JPG/WebP blob at the given scale.
function rasterizeSvg(svg, { width, height, scale, mime, background }) {
  return new Promise((resolve, reject) => {
    const url = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const ctx = canvas.getContext("2d");
      // JPEG has no alpha — paint a solid background (the export bg, else white).
      if (mime === "image/jpeg") {
        ctx.fillStyle = background && background !== "transparent" ? background : "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else if (background && background !== "transparent") {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
        mime,
        mime === "image/jpeg" ? 0.92 : undefined
      );
    };
    img.onerror = () => reject(new Error("SVG rasterization failed"));
    img.src = url;
  });
}

// Export a set of elements (single shape or a marquee group) as svg / png / jpg
// / webp. Raster formats render a clean static snapshot (no animations); SVG
// keeps the interactive animations embedded.
export async function exportSelection(elements, { format = "png", scale = 1, background = "transparent", name = "svg-studio-selection" } = {}) {
  if (!elements || !elements.length) throw new Error("No elements to export");
  const frame = contentFrame(elements);
  const fmt = EXPORT_FORMATS.find((f) => f.value === format) || EXPORT_FORMATS[1];

  if (fmt.value === "svg") {
    const svg = buildSvgMarkup(elements, { ...frame, playing: true, background });
    triggerDownload(new Blob([svg], { type: "image/svg+xml" }), `${name}.svg`);
    return;
  }

  const svg = buildSvgMarkup(elements, { ...frame, playing: false, background: "transparent", includeAnimations: false });
  const blob = await rasterizeSvg(svg, { width: frame.width, height: frame.height, scale, mime: fmt.mime, background });
  const suffix = scale > 1 ? `@${scale}x` : "";
  triggerDownload(blob, `${name}${suffix}.${fmt.ext}`);
}

export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Build a sharable link by encoding the project into the URL hash.
export function buildShareLink(elements, meta = {}) {
  const payload = JSON.stringify({ v: 1, meta, elements });
  // base64url
  const b64 = btoa(unescape(encodeURIComponent(payload)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  const url = `${window.location.origin}${window.location.pathname}#p=${b64}`;
  return url;
}

export function parseShareLink() {
  if (!window.location.hash.startsWith("#p=")) return null;
  try {
    const b64 = window.location.hash.slice(3).replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(escape(atob(b64)));
    return JSON.parse(json);
  } catch {
    return null;
  }
}
